import type { QuestCompletions } from "@/components/QuestsV2/types";
import { RedemptionCountdown } from "@/components/WeeklyRewards/RedemptionInfo";
import { useWeeklyRewards } from "@/components/WeeklyRewards/WeeklyRewardsProvider";
import { Button } from "@/components/ui/button";
import { ONBOARDING_QUEST_ID } from "@/constants";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { ArrowRight, Clock } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { Progress } from "../../ui/progress";
import type { ActivationQuest } from "./ActivationPanel.utils";
import { QuestType } from "./ActivationPanel.utils";
import { OfferCountdown } from "./OfferCountdown";
import { QuestItem } from "./QuestItem";

export type ActivationPanelProps = {
  banner: string;
  title: string;
  description: string;
  onBannerClick?: () => void;
  completedQuestsCount: number;
  totalQuestsCount: number;
  activationQuests: ActivationQuest[];
  isCompleted: boolean;
  completedQuests?: QuestCompletions;
  spinsAvailable?: number;
};

export const ActivationPanel = ({
  banner,
  title,
  description,
  completedQuestsCount,
  totalQuestsCount,
  activationQuests,
  onBannerClick,
  isCompleted,
  completedQuests,
  spinsAvailable,
}: ActivationPanelProps) => {
  const { sweepstake } = useWeeklyRewards();
  const router = useRouter();
  const { track } = useOnboardingAnalytics();
  const { activationSpinnerRewardsOct25 } = useFeatureFlags();

  const isSpinnerRewardsVariant = activationSpinnerRewardsOct25 === "variant";
  const showSweepstakesCountdown =
    sweepstake?.current?.endDate && !isSpinnerRewardsVariant;
  const sweepstakesCountdownValue = new Date(
    sweepstake?.current?.endDate as string,
  );

  // Calculate offer expiration date (48 hours after onboarding completion for variant)
  const offerExpirationDate = useMemo(() => {
    if (activationSpinnerRewardsOct25 === "variant") {
      const onboardingQuestCompletionTime =
        completedQuests?.[ONBOARDING_QUEST_ID.replace("-event", "")]
          ?.latest_completion_time;

      if (onboardingQuestCompletionTime) {
        const completionDate = new Date(onboardingQuestCompletionTime);
        const completionTime = completionDate.getTime();
        const currentTime = Date.now();
        const fortyEightHoursInMs = 2 * 24 * 60 * 60 * 1000;

        // Check if offer is still valid (within 48 hours of completion)
        if (completionTime > currentTime - fortyEightHoursInMs) {
          // Add 48 hours to completion time to get expiration date
          const expirationDate = new Date(completionTime + fortyEightHoursInMs);
          return expirationDate;
        }
      }
    }
    return undefined;
  }, [activationSpinnerRewardsOct25, completedQuests]);

  const showOfferCountdown = isSpinnerRewardsVariant && offerExpirationDate;

  const handleNavigationClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "activation-panel",
        control: "keep-questing",
        controlType: ControlType.BUTTON,
      },
    });
    router.push("/quests#onboarding-quests__section");
  };

  // Use large banner for desktop in spinner rewards variant
  const desktopBanner =
    activationSpinnerRewardsOct25 === "variant"
      ? "/images/prize-wheel-activation-banner-large.webp"
      : banner;

  const spinnerDescription =
    "Complete to unlock spins and win up to $5,000 in prizes!";

  return (
    <div className="@container">
      <div className="flex flex-col gap-x-2 @3xl:flex-row font-suisse mb-8 relative p-3 w-full space-x-0 @3xl:space-x-4 backdrop-blur-md bg-neutral-900/50 overflow-hidden rounded-xl">
        {/* Banner Image for Desktop */}
        <div className="relative @3xl:block hidden rounded-xl overflow-hidden">
          <Image
            src={desktopBanner}
            alt="Activation Banner"
            width={1536}
            height={1024}
            className="aspect-video w-full h-full object-cover"
            onClick={onBannerClick}
          />
          {/* Text overlay for variant banner */}
          {isSpinnerRewardsVariant && (
            <div className="absolute inset-0 flex items-start justify-start pt-20 pl-8 pointer-events-none">
              <div className="space-y-1">
                <h1 className="text-4xl font-bold text-white">
                  $5K Prize Wheel
                </h1>
                <p className="text-sm text-white max-w-sm">
                  {spinnerDescription}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2 flex-grow @3xl:min-w-96">
          {/* Header */}
          <div className="px-3 @3xl:pl-0 py-1 rounded-xl space-y-2">
            <div className="flex flex-col gap-y-1">
              {/* Show offer expiration countdown for variant */}
              {showOfferCountdown ? (
                <div className="flex flex-row gap-x-2 items-center text-status-success">
                  <Clock className="size-4" />
                  <div className="flex flex-row gap-x-1 items-center">
                    <p className="text-xs font-bold">Offer expires in</p>
                    <OfferCountdown targetDate={offerExpirationDate} />
                  </div>
                </div>
              ) : null}

              <h2 className="text-xl font-bold text-white">{title}</h2>
              {isSpinnerRewardsVariant ? (
                <p className="@3xl:hidden text-xs text-[#B6B6B6] max-w-sm">
                  {spinnerDescription}
                </p>
              ) : null}

              {/* Show sweepstakes countdown for control */}
              {showSweepstakesCountdown ? (
                <div className="flex flex-row gap-x-2 items-center">
                  <p className="text-xs text-white/70">{description}</p>
                  <RedemptionCountdown
                    hideSeconds
                    gap="base.spacing.x1"
                    countdownValue={sweepstakesCountdownValue}
                    size={"small"}
                  />
                </div>
              ) : null}
            </div>

            <div className="flex gap-x-2 items-center">
              <Progress
                className="bg-neutral-100/15"
                indicatorClassName="rounded-r-none bg-status-success"
                value={completedQuestsCount === 0 ? 0.15 : completedQuestsCount}
                max={totalQuestsCount}
              />
              <h3 className="text-status-success text-xs leading-3 font-medium px-1.5 py-1 rounded-md border-status-success border">
                {completedQuestsCount}/{totalQuestsCount}
              </h3>
            </div>
          </div>

          {/* Banner Image for Mobile */}
          <div className="relative @3xl:hidden block rounded-lg overflow-hidden border border-white/20">
            <Image
              src={banner}
              alt="Activation Banner"
              width={1536}
              height={1024}
              className="aspect-[28/9] w-full h-full object-cover object-top"
              onClick={onBannerClick}
            />
            {/* Text overlay for variant banner */}
            {isSpinnerRewardsVariant && (
              <div className="absolute inset-0 flex items-start justify-end pt-2 pr-3 pointer-events-none">
                <h1 className="font-bold text-white text-[clamp(0.875rem,3cqw,1.25rem)]">
                  $5K in Prizes!
                </h1>
              </div>
            )}
          </div>

          {/* Quests List */}
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden space-y-px border border-white/10">
              {activationQuests.map((quest, index) => {
                // Find the first incomplete quest (excluding bonus/free spin)
                const firstIncompleteIndex = activationQuests.findIndex(
                  (q) =>
                    !q.completed &&
                    q.type !== QuestType.BONUS_GEMS &&
                    q.type !== QuestType.FREE_SPIN,
                );
                const isNextQuest = index === firstIncompleteIndex;

                return (
                  <QuestItem
                    key={`${quest.name}-${quest.type}`}
                    quest={quest}
                    allQuestsCompleted={isCompleted}
                    spinsAvailable={spinsAvailable}
                    isNextQuest={isNextQuest}
                  />
                );
              })}
            </div>
            {isCompleted ? (
              <Button
                size="rectangle"
                className="w-full"
                onClick={handleNavigationClick}
              >
                Keep Questing
                <ArrowRight className="absolute size-5 right-8" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
